// -----------------------------------------------------------------------------
// Library
// -----------------------------------------------------------------------------
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, DatePicker, Input, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
// -----------------------------------------------------------------------------
// Components
// -----------------------------------------------------------------------------
import GymBanner from "components/UI/GymBanner";
import WorkoutsList from "components/Workouts/WorkoutsList";
import { CategoriesFilter } from "components/Categories/CategoriesFilter";
import { PhoneNumberAlert, StripeSubscriptionAlerts } from "components/Alerts";
// -----------------------------------------------------------------------------
// Actions and helpers
// -----------------------------------------------------------------------------
import { updateMembersWorkoutsSearchTerm } from "store/slices";
import { selectMembersWorkoutsSearchTerm } from "store/selectors";
import usePrimaryColor from "helpers/hooks/usePrimaryColor";
// -----------------------------------------------------------------------------
// Styles
// -----------------------------------------------------------------------------
import StyledOnDemand from "./styles";
import { Flex } from "antd/lib";
import moment from "moment";
// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
const OnDemand = ({ isNormalUser }) => {
	const { RangePicker } = DatePicker;
	const dispatch = useDispatch();
	const primaryColor = usePrimaryColor();
	const searchTerm = useSelector(selectMembersWorkoutsSearchTerm);

	const dateRangeInitState = {
		startDate: "",
		endDate: "",
	};

	const [dateRange, setDateRange] = useState(dateRangeInitState);

	const onDateRangeChange = (dates) => {
		if (dates) {
			setDateRange({
				startDate: dates[0],
				endDate: dates[1],
			});
		}
	};

	const handleSearch = (e) => {
		const { value } = e.target;
		dispatch(updateMembersWorkoutsSearchTerm(value?.toLowerCase?.() || ""));
	};

	const resetDates = () => {
		setDateRange(dateRangeInitState);
	};

	const isVisibleDateChip = dateRange.startDate && dateRange.endDate;

	return (
		<StyledOnDemand className="sb-on-demand-container">
			<GymBanner>{isNormalUser && <StripeSubscriptionAlerts />}</GymBanner>

			<div className="sb-on-demand-container__body">
				<div className="sb-on-demand-container__body__head">
					<h1 className="sb-on-demand-container__body__head__title">Explore Videos</h1>
					<div className="sb-on-demand-container__body__head__range-picker"></div>
				</div>
				<Row className="sb-on-demand-container__body__filters" gutter={[8, 16]} data-id="sectionDateFilter">
					<Col md={8} xs={24}>
						<Input
							className="search-input"
							prefix={<SearchOutlined />}
							onChange={handleSearch}
							placeholder="Search Workouts..."
							style={{ minWidth: 256, "--primary-color": primaryColor }}
							data-id="txtWorkoutSearch"
							value={searchTerm}
							allowClear
						/>
					</Col>
					<Col md={16} xs={24}>
						<Flex justify="end">
							<RangePicker
								value={[dateRange.startDate, dateRange.endDate]}
								allowClear={false}
								popupStyle={{ zIndex: 99999 }}
								presets={[
									{
										label: "Today",
										value: [dayjs().startOf("day"), dayjs().endOf("day")],
									},
									{
										label: "This Week",
										value: [dayjs().startOf("week"), dayjs().endOf("week")],
									},
									{
										label: "Last Month",
										value: [
											dayjs().startOf("month").subtract(1, "months"),
											dayjs().startOf("month").subtract(1, "days"),
										],
									},
									{
										label: "This Month",
										value: [dayjs().startOf("month"), dayjs().endOf("month")],
									},
									{
										label: "Last Year",
										value: [dayjs().startOf("year").subtract(1, "years"), dayjs().startOf("year").subtract(1, "days")],
									},
									{
										label: "This Year",
										value: [dayjs().startOf("year"), dayjs().endOf("year")],
									},
								]}
								onChange={onDateRangeChange}
								placement="bottomRight"
								popupClassName="custom-picker-range-popup-resposive"
								getPopupContainer={(trigger) => trigger.parentNode}
								data-id="datePickerFilter"
							/>
						</Flex>
					</Col>
				</Row>
				{dateRange.startDate && dateRange.endDate && (
					<Tag
						closable={isVisibleDateChip}
						onClose={resetDates}
						bordered={false}
						style={{
							margin: "12px 0 36px",
							backgroundColor: `var(--background-color)`,
							borderRadius: 100,
							padding: "1px 8px",
						}}
					>
						<span
							style={{
								color: `var(--primary-color)`,
							}}
						>
							{moment(dateRange.startDate.toDate()).format("YYYY-MM-DD")} -{" "}
							{moment(dateRange.endDate.toDate()).format("YYYY- MM-DD")}
						</span>
					</Tag>
				)}
				{isNormalUser && <PhoneNumberAlert />}
				<div className="sb-on-demand-container__body__filters" data-id="listTags">
					<CategoriesFilter />
				</div>
				<WorkoutsList dateRange={dateRange} />
			</div>
		</StyledOnDemand>
	);
};

export default OnDemand;
